/**
 * Shared API client for authenticated BFF requests.
 * Used by main and dashboard MFEs.
 */

import {
    getMFEBackendServerURLPrefix,
    getServerConfig
} from '@shared/config/server-config';
import { getPsSessionToken } from './auth-service';

export interface ApiError {
    message: string;
    status: number;
}

export function getAuthHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        // API Gateway uses Authorization as the authorizer identity source so
        // header- and cookie-authenticated browser requests both reach the
        // Lambda authorizer.
        Authorization: 'SessionContext'
    };
    const sessionId = getServerConfig().SESSION_ID;
    if (sessionId) {
        headers['X-Session-Ref'] = sessionId;
    }
    // Include PS session token via header when cookie-based sharing
    // is not available (e.g., dev environments with domain mismatch)
    const psToken = getPsSessionToken();
    if (psToken) {
        headers['X-Session-Token'] = psToken;
    }
    return headers;
}

export function getApiUrlPrefix(): string {
    if (typeof window !== 'undefined' && window.location) {
        const originalHostname = window.location.hostname;
        if (
            originalHostname === 'localhost' ||
            originalHostname === '127.0.0.1' ||
            originalHostname === '[::1]'
        ) {
            return `${window.location.origin}/api`;
        }
    }

    const configuredPrefix = getMFEBackendServerURLPrefix();
    if (configuredPrefix) return configuredPrefix;

    if (typeof window !== 'undefined' && window.location) {
        let hostname = window.location.hostname;
        const prefixesToStrip = ['portal.', 'smc.', 'api.', 'www.'];
        let previousHostname = '';
        while (hostname !== previousHostname) {
            previousHostname = hostname;
            for (const prefix of prefixesToStrip) {
                if (hostname.startsWith(prefix)) {
                    hostname = hostname.substring(prefix.length);
                    break;
                }
            }
        }
        return `${window.location.protocol}//api.${hostname}`;
    }
    return '';
}

export async function extractErrorMessage(response: Response): Promise<string> {
    let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
    try {
        const errorBody = await response.json();
        if (errorBody.message) errorMessage = errorBody.message;
        else if (errorBody.error) errorMessage = errorBody.error;
    } catch {
        // ignore
    }
    return errorMessage;
}

export async function makeApiRequest(
    path: string,
    options: RequestInit = {}
): Promise<Response> {
    const urlPrefix = getApiUrlPrefix();
    const apiUrl = `${urlPrefix}${path}`;
    return fetch(apiUrl, {
        ...options,
        credentials: 'include',
        headers: { ...getAuthHeaders(), ...(options.headers || {}) }
    });
}
